import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { getJson, postJson } from '@/api/client'
import type { LiveTestConversationDetail, LiveTestRunResponse } from '@/api/types'
import { Badge } from '@/ui/primitives'

function catBadge(cat: string | null): 'default' | 'success' | 'warning' | 'danger' {
  if (cat === 'complaint') return 'danger'
  if (cat === 'order_status') return 'warning'
  if (cat === 'beauty') return 'success'
  return 'default'
}

function catLabel(cat: string | null): string {
  if (!cat) return ''
  const m: Record<string, string> = {
    beauty: 'Beauty', order_status: 'Order', complaint: 'Complaint',
    pricing: 'Pricing', product_availability: 'Avail.', store_info: 'Store',
    how_to_use: 'How-to', social_media: 'Social', general: 'General',
  }
  return m[cat] || cat
}

function formatTs(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })
}

interface LiveTestSelectorProps {
  title?: string
  apiBase?: string
  routeBase?: string
  backLabel?: string
}

export function LiveTestSelector({
  title = 'Live Test',
  apiBase = '/v1/admin/live-test',
  routeBase = '/admin/live-test',
  backLabel = 'Back to Live Test',
}: LiveTestSelectorProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const convId = id ? parseInt(id, 10) : 0

  const [detail, setDetail] = useState<LiveTestConversationDetail | null>(null)
  const [running, setRunning] = useState(false)
  const [runningTurn, setRunningTurn] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void loadDetail() }, [convId])

  async function loadDetail() {
    try {
      const data = await getJson<LiveTestConversationDetail>(`${apiBase}/conversations/${convId}`)
      setDetail(data)
      setError(null)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleRunFrom(turnNum: number) {
    setRunning(true)
    setRunningTurn(turnNum)
    setError(null)
    try {
      await postJson<LiveTestRunResponse>(`${apiBase}/run`, {
        conversation_id: convId,
        start_turn: turnNum,
      })
      navigate(`${routeBase}/${convId}/compare/${turnNum}`)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setRunning(false)
      setRunningTurn(null)
    }
  }

  if (!convId) {
    return <div className="flex items-center justify-center min-h-screen text-mute text-sm">Invalid conversation ID</div>
  }

  if (!detail) {
    return <div className="flex items-center justify-center min-h-screen text-mute text-sm">loading…</div>
  }

  const conv = detail.conversation
  const turns = detail.turns
  const customerTurns = turns.filter((t) => t.customer_message || t.attachment_json)

  return (
    <div className="px-8 lg:px-14 py-12 h-full overflow-y-auto">
      <div className="mb-8">
        <button onClick={() => navigate(routeBase)} className="text-xs text-mute hover:text-cream transition-colors mb-3 block">
          ← {backLabel}
        </button>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="font-display text-3xl font-medium text-cream tracking-tight">
            {conv.customer_name || `User #${conv.remote_from_id}`}
          </h1>
          {conv.primary_category && (
            <Badge variant={catBadge(conv.primary_category)}>{catLabel(conv.primary_category)}</Badge>
          )}
        </div>
        <p className="text-sm text-mute mt-1">
          {title} · {conv.total_human_messages} turns · {customerTurns.length} customer messages
          {conv.customer_phone && ` · ${conv.customer_phone}`}
        </p>
      </div>

      {error && (
        <div className="mb-6 font-display italic text-sm border-l-[3px] border-ember text-ember pl-4 py-2 bg-ember/5">
          {error}
        </div>
      )}

      <div className="border border-haze/30 bg-ink-raised overflow-hidden">
        <div className="divide-y divide-haze/20">
          {turns.length === 0 ? (
            <div className="px-5 py-12 text-center text-mute text-xs">No turns in this conversation.</div>
          ) : (
            turns.map((turn) => {
              const isCustomer = !!(turn.customer_message || turn.attachment_json)
              return (
                <div key={turn.id} className={`px-4 py-3 flex items-start gap-4 ${isCustomer ? '' : 'bg-ink/10'}`}>
                  <div className={`w-16 flex-shrink-0 text-[10px] text-mute/50 font-mono pt-0.5`}>
                    {turn.sequence}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-medium uppercase tracking-wider ${isCustomer ? 'text-leaf/70' : 'text-ember/50'}`}>
                        {isCustomer ? 'Customer' : 'Agent'}
                      </span>
                      <span className="text-[10px] text-mute/40">{formatTs(turn.customer_message_ts)}</span>
                      {turn.category && (
                        <Badge variant={catBadge(turn.category)}>{catLabel(turn.category)}</Badge>
                      )}
                    </div>
                    {isCustomer ? (
                      <div className="text-xs text-cream whitespace-pre-wrap leading-relaxed break-words bg-ink-raised border border-haze/40 px-3 py-2">
                        {turn.customer_message || <span className="italic text-cream/30">[Image]</span>}
                      </div>
                    ) : (
                      <div className="text-xs text-cream/80 whitespace-pre-wrap leading-relaxed break-words bg-ember/[0.04] border border-ember/8 px-3 py-2">
                        {turn.human_agent_response || '—'}
                        {turn.human_agent_name && (
                          <div className="text-[10px] text-ember/50 mt-1">{turn.human_agent_name}</div>
                        )}
                      </div>
                    )}
                  </div>
                  {isCustomer && (
                    <div className="flex-shrink-0 pt-1">
                      <button
                        onClick={() => handleRunFrom(turn.sequence)}
                        disabled={running}
                        className={`px-3 py-1 text-[10px] transition-colors ${
                          running && runningTurn === turn.sequence
                            ? 'bg-ember/20 text-ember/50 cursor-wait'
                            : 'border border-emerald/30 text-emerald/70 hover:bg-emerald/10 hover:text-emerald/90'
                        }`}
                      >
                        {running && runningTurn === turn.sequence ? 'Running…' : '▶ Run'}
                      </button>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

export function RealChatReplaySelector() {
  return (
    <LiveTestSelector
      title="Real Chat Replay"
      apiBase="/v1/admin/real-chat-replay"
      routeBase="/admin/real-chat-replay"
      backLabel="Back to Real Chat Replay"
    />
  )
}
